import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * The storefront's Button, on shadcn's recipe but the merchant's palette.
 *
 * Every colour here is a §5 design token, so a shop switching from Sakura to
 * Noir repaints this component at runtime with no rebuild. Nothing in this
 * file may name a colour.
 *
 * `min-h-11` rather than shadcn's default 36px: this is a checkout used
 * one-handed on a phone, often outdoors, and the "Buy" button is the single
 * most expensive thing on the page to mis-tap.
 */
const buttonVariants = cva(
  'inline-flex w-full items-center justify-center gap-2 font-semibold transition-[background-color,opacity] ' +
    'rounded-[var(--radius)] min-h-11 px-4 text-[0.95rem] ' +
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)] ' +
    'disabled:opacity-45 disabled:cursor-not-allowed ' +
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-[1.15em] [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          'bg-[var(--color-primary)] text-[var(--color-primary-contrast)] hover:bg-[var(--color-primary-hover)] active:bg-[var(--color-primary-hover)]',
        ghost:
          'bg-transparent text-[var(--color-text)] border border-[var(--color-border)] hover:bg-[var(--color-surface)]',
        soft: 'bg-[var(--color-primary-soft)] text-[var(--color-primary)] hover:opacity-90',
        quiet: 'bg-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
      },
      width: {
        full: 'w-full',
        auto: 'w-auto',
      },
    },
    defaultVariants: { variant: 'primary', width: 'full' },
  },
);

export function Button({
  className,
  variant,
  width,
  asChild = false,
  ...props
}: ComponentProps<'button'> & VariantProps<typeof buttonVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, width }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
